import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import Select from 'react-select';
import { Label, Button, CardBody, Col, Collapse, Container, Input, Row, Spinner, Tooltip } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    SystemReportParameter, getReportYears, getSystemReport, selectAvailableReportYears, selectReportGroup,
    selectReportStatus, getMax939Year, refreshLongRunningReport
} from '../../redux/ReportSlice';
import { TaxonomyTypeEnum } from "../../redux/TaxonomyVersionSlice";
import { UserRoleEnum, selectUserLogged } from '../../redux/UserSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


export interface IReportFormat {
    value: 'XHTML' | 'IXBRL' | 'PDF' | 'EXCEL' | 'XBRL' | 'UNKNOWN';
    label: string;
}

export interface ISystemReport {
    value: string,
    label: string,
    adminOnly: boolean,
    group: string,
    documentation: string,
    effectiveYear: number,
    outputRestrictions: string | null,
    icon: string,
    iconOverride?: any
}

export const reportFormats: Array<IReportFormat> = [
    { label: 'PDF', value: 'PDF' },
    { label: 'Excel', value: 'EXCEL' },
    { label: 'XHTML', value: 'XHTML' },
    { label: 'IXBRL', value: 'IXBRL' }];

export const defaultReportFormatValue: IReportFormat = { value: 'UNKNOWN', label: '' };

export const defaultValue = { value: '', label: '' };

export const getSuffix = function (formatType: string): string {
    var suffix = "";
    switch (formatType) {
        case 'PDF':
            suffix = '.pdf';
            break;
        case 'EXCEL':
            suffix = '.xlsx';
            break;
        case 'XHTML':
            suffix = '.xhtml';
            break;
        case 'IXBRL':
            suffix = '.xhtml';
            break;
    }
    return suffix;
}

export const monthOptions: Array<{ value: string; label: string; }> = [
    { value: "0", label: "Select A Month" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

export const availableReports: ISystemReport[] = [
    {
        value: 'BALANCESHEETACCOUNTS', label: 'Balance Sheet Code List', adminOnly: false,
        documentation: "This report contains a list of Balance Sheet Account Codes available for the selected year.", effectiveYear: 2022, outputRestrictions: null,
        icon: "bi-list-ol", group: "CODES"
    },
    {
        value: 'BALANCESHEETDETAILREPORT', label: 'Balance Sheet Details', adminOnly: false,
        documentation: "This report contains Balance Sheet information submitted by Florida’s local governments.", effectiveYear: 2022, outputRestrictions: null,
        icon: "bi-clipboard-data", group: "DETAILS"
    },
    {
        value: 'BALANCESHEETDETAILREPORTUNCERTIFIED', label: 'Balance Sheet Details (includes unverified data)', adminOnly: true,
        documentation: "This report contains Balance Sheet information submitted by Florida’s local governments. All submitted financial data is shown.", effectiveYear: 2022, outputRestrictions: null,
        icon: "bi-file-spreadsheet", group: "DETAILS"
    },
    {
        value: 'REVACCOUNTS', label: 'Revenue Code List', adminOnly: false,
        documentation: "This report contains a list of Revenue Account Codes available for the selected year.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-list-task", group: "CODES"
    },
    {
        value: 'REVENUEDETAILREPORT', label: 'Revenue Details', adminOnly: false,
        documentation: "This report contains Revenue information submitted by Florida’s local governments.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-clipboard-plus", group: "DETAILS"
    },
    {
        value: 'REVENUEDETAILREPORTUNCERTIFIED', label: 'Revenue Details (includes unverified data)', adminOnly: true,
        documentation: "This report contains Revenue information submitted by Florida’s local governments. All submitted financial data is shown.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-file-spreadsheet", group: "DETAILS"
    },
    {
        value: 'EXPENDACCOUNTS', label: 'Expenditure Code List', adminOnly: false,
        documentation: "This report contains a list of Expenditure Account Codes available for the selected year.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-list-ul", group: "CODES"
    },
    {
        value: 'EXPENDITUREDETAILREPORT', label: 'Expenditure Details', adminOnly: false,
        documentation: "This report contains Expenditure information submitted by Florida's local governments.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-clipboard-minus", group: "DETAILS"
    },
    {
        value: 'EXPENDITUREDETAILREPORTUNCERTIFIED', label: 'Expenditure Details (includes unverified data)', adminOnly: true,
        documentation: "This report contains Expenditure information submitted by Florida’s local governments. All submitted financial data is shown.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-file-spreadsheet", group: "DETAILS"
    },
    {
        value: 'TOTALREVEXPDEBT', label: 'Total Revenues, Expenditures and Debt', adminOnly: false,
        documentation: "This report contains Revenue, Expenditure, and Debt information submitted by Florida’s local governments.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-cash", group: "DETAILS"
    },
    {
        value: 'PRELIMINARYTOTALSREPORT', label: 'Total Revenues, Expenditures and Debt (includes unverified data)', adminOnly: true,
        documentation: "This report contains Revenue, Expenditure, and Debt information submitted by Florida’s local governments.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-cash", group: "DETAILS"
    },
    {
        value: 'COMPLIANTGOVS', label: 'Master Compliant Report', adminOnly: true,
        documentation: "This report lists all of Florida’s local government entities who have submitted their Annual Financial Reports prior to the 9-month deadline, and when the Annual Financial Reports and audits were submitted. This report also lists Special Districts’ Dependency and Types.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-check-circle", group: "REG"
    },
    {
        value: 'NONCOMPLIANTGOVS', label: 'Master Non-Compliant Report', adminOnly: true,
        documentation: "This report lists Florida’s local government entities who did not submit their Annual Financial Report within the timeline stipulated by Florida Statutes. This report includes if the Annual Financial Report was not completed within 45 days of the Audit’s Report Date.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-x-circle", group: "REG"
    },
    {
        value: 'PUBLICCOMPLIANTGOVS', label: 'Compliant Report', adminOnly: false,
        documentation: "This report lists all of Florida’s local government entities who have submitted their Annual Financial Reports prior to the 9-month deadline, and when the Annual Financial Reports and audits were submitted.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-check-circle", group: "REG"
    },
    {
        value: 'PUBLICNONCOMPLIANTGOVS', label: 'Non-Compliant Report', adminOnly: false,
        documentation: "This report lists Florida’s local government entities who did not submit their Annual Financial Report within the timeline stipulated by Florida Statutes.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-x-circle", group: "REG"
    },
    {
        value: 'NONCOMPLIANTCONSECUTIVE', label: 'Non-Compliant Municipalities For Consecutive Years', adminOnly: false,
        documentation: "The report lists municipalities that have not reported an expenditures or revenues for two or more consecutive years. This includes municipalities who have not submitted their Annual Financial Report for two or more consecutive years.", effectiveYear: 2021, outputRestrictions: null,
        icon: "bi-building-x", group: "REG"
    },
    {
        value: 'DATAELEMENTWORKSHEET', label: 'Auditor General Report', adminOnly: true,
        documentation: "This report lists Florida’s local government entities who submitted Data Element Worksheets, and what financial items were listed.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-exclamation-diamond", group: "REG"
    },
    {
        value: 'AFRSTATUSES', label: 'AFR Status Report', adminOnly: true,
        documentation: "This report lists all active local government entities, what status their AFR is in, and if they have an audit.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-thin fa-copy", group: "GENERALADMIN", iconOverride: "fa-copy"
    },
    {
        value: 'INVALIDAFRS', label: 'Invalid AFR Status Report', adminOnly: true,
        documentation: "", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-thin fa-copy", group: "GENERALADMIN", iconOverride: "fa-regular fa-copy"
    },
    {
        value: 'ENTITYLISTING', label: 'Entity Listing', adminOnly: true,
        documentation: "This report lists all active Florida local government entities, their contacts who access their Annual Financial Report, and their contacts’ information, such as name, email, and phone number.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-regular fa-address-book", group: "GENERALADMIN", iconOverride: "fa-building"
    },
    {
        value: 'COURTREPORTINGENTITYLISTING', label: 'Entity Listing for Court Reporting', adminOnly: true,
        documentation: "This report lists all active Florida local government entities, their contacts who access their 939.185 and 29.0085 Court Reports, and their contacts’ information, such as name, email, and phone number.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-light fa-address-book", group: "COURT_REPORTING", iconOverride: "fa-address-book"
    },
    {
        value: 'ENTITYREPORTLISTING', label: 'Entity Report Listing', adminOnly: true,
        documentation: "This report lists all Florida local government entities, including those that have been dissolved or inactivated, and what reports they have access to.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-building", group: "GENERALADMIN"
    },
    {
        value: 'CRAZERO', label: 'Special District Zero', adminOnly: true,
        documentation: "This report lists any of Florida’s Special Districts that have reported $0 in either Revenues, Expenditures, or Debt.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-0-circle", group: "SD"
    },
    {
        value: 'FORTYFIVEDAYNORESPONSE', label: '45 Day No Response', adminOnly: true,
        documentation: "This report lists local government entities that have had their Annual Financial Reports returned and have NOT re-certified the report within 45 days, as required by statute.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-solid fa-rotate-left", group: "REG", iconOverride: "fa-clock-rotate-left"
    },
    {
        value: 'JLAC', label: 'JLAC Flag', adminOnly: false,
        documentation: "This report lists what Florida local governments have not submitted their Annual Financial Report per statute. Furthermore, it lists when the enforcement letter was dated and when funds will be withheld from the local government entity.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-flag", group: "REG"
    },
    {
        value: 'NONCOMPLIANTSD', label: 'Non-Compliant Special Districts', adminOnly: true,
        documentation: "This report lists any of Florida’s Special Districts that did not file their reports before the 9-month deadline and Special Districts whose Annual Financial Report was not completed within 45 days of the Audit’s Report Date.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-x-circle", group: "REG"
    },
    {
        value: 'DWELLINGIMPACTFEE', label: 'Total Impact Fees per Dwelling', adminOnly: false,
        documentation: "This report lists entities who collected Impact Fees, what Impact Fees they collected, and the finances they generated.", effectiveYear: 2020, outputRestrictions: null,
        icon: "bi-building", group: "DETAILS"
    },
    {
        value: 'SPECIALDISTRICTBONDS', label: 'Special District Bonds', adminOnly: false,
        documentation: "This report lists the different Bonds, the Maturity Dates, and the Outstanding Balances from Florida's Special Districts.", effectiveYear: 2021, outputRestrictions: null,
        icon: "bi-cash-stack", group: "SD"
    },
    {
        value: 'SPECIALDISTRICTCONSTRUCTIONPROJECTS', label: 'Special District Construction Projects', adminOnly: false,
        documentation: "This report lists the different Construction Projects, Expenditures, and Budget from Florida’s Special Districts.", effectiveYear: 2021, outputRestrictions: null,
        icon: "bi-cone-striped", group: "SD"
    },
    {
        value: 'SPECIALDISTRICTEMPLOYEESANDCONTRACTORS', label: 'Special District Employees and Contractors', adminOnly: false,
        documentation: "This report lists Employees, Contractors, and their Compensation from Florida’s Special Districts.", effectiveYear: 2021, outputRestrictions: null,
        icon: "bi-person-badge", group: "SD"
    },
    {
        value: 'SPECIALDISTRICTMILLAGEANDASSESSMENTRATES', label: 'Special District Millage and Assessment Rates', adminOnly: false,
        documentation: "This report lists Employees, Contractors, and their Compensation from Florida’s Special Districts.", effectiveYear: 2021, outputRestrictions: null,
        icon: "bi-card-list", group: "SD"
    },
    {
        value: 'SUBMITTEDORVERIFIEDWITHVALIDATIONERRORS', label: 'Submitted or Verified AFRs That Fail Validation', adminOnly: true,
        documentation: "This report lists Florida’s local government entities that have submitted Annual Financial Reports that, due to system glitches, may have information that needs to be updated to be meet submission requirements.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-light fa-ban", group: "GENERALADMIN", iconOverride: "fa-ban"
    },
    {
        value: 'AFRSSUBMITTEDWITHEMERGENCY', label: 'Financial Emergency Report', adminOnly: true,
        documentation: "This report lists all local government entities who have entered a state of financial emergency per statute.", effectiveYear: 2012, outputRestrictions: null,
        icon: "bi-file-medical", group: "REG"
    },
    {
        value: 'CR_939_PUBLICREPORT', label: 'Court Reporting 939.185 Public Report', adminOnly: false,
        documentation: "This report details the additional court costs that the county commissioners collect per 939.185, F.S. and how those costs are allocated to the different court programs.", effectiveYear: 2012, outputRestrictions: "PDF",
        icon: "bi-bank", group: "COURT_REPORTING", iconOverride: "fa-scale-unbalanced-flip"
    },
    {
        value: 'CR_29_PUBLICREPORT', label: 'Court Reporting 29.0085 Public Report', adminOnly: false,
        documentation: "This report details the revenues and expenditures incurred by county courts per 29.0085, F.S.", effectiveYear: 2012, outputRestrictions: "PDF",
        icon: "bi-bank2", group: "COURT_REPORTING", iconOverride: "fa-scale-unbalanced"
    },
    {
        value: 'SURVEYRESULTS', label: 'Survey Results Report', adminOnly: true,
        documentation: "This report details the survey results", effectiveYear: 2024, outputRestrictions: "EXCEL",
        icon: "bi-clipboard-data", group: "GENERALADMIN"
    },
    {
        value: 'CR_29_STATUSES', label: 'CR 29 Status Report', adminOnly: true,
        documentation: "This report lists all active local government entities, what status their CR_29 is in, and if they have an audit.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-thin fa-copy", group: "COURT_REPORTING", iconOverride: "fa-copy"
    },
    {
        value: 'CR_939_STATUSES', label: 'CR 939 Status Report', adminOnly: true,
        documentation: "This report lists all active local government entities, what status their CR_939 is in, and if they have an audit.", effectiveYear: 2012, outputRestrictions: null,
        icon: "fa-thin fa-copy", group: "COURT_REPORTING", iconOverride: "fa-copy"
    }
];

const SystemReports = () => {

    const dispatch = useAppDispatch();
    const reportGroup: string = useAppSelector(selectReportGroup);
    const availableReportYears = useAppSelector(selectAvailableReportYears);
    const user = useAppSelector(selectUserLogged);
    const selectedReportStatus = useAppSelector(selectReportStatus);
    const defaultReport: ISystemReport = {
        label: "default",
        value: "default",
        adminOnly: false,
        group: "ALL",
        documentation: "",
        effectiveYear: 2012,
        icon: "",
        outputRestrictions: ""
    };



    var reportsByName: any | null = null;
    var reportsByValue: any | null = null;

    const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false);
    const [reportGenerationStatus, setReportGenerationStatus] = React.useState<string>("");
    const [modalSubmissionIsOpen, setModalSubmissionIsOpen] = React.useState(false);

    const [max939YearValue, setMax939YearValue] = React.useState<number>(0);

    const maxAvailableReportYearValue = Math.max(...availableReportYears);

    const handleCloseSubmissionModal = function () {
        setModalSubmissionIsOpen(false);
    }

    const getReportByName = function (name: string): ISystemReport | null {
        var result: ISystemReport | null = null;
        if (!reportsByName) {
            var newmap: any = {};
            availableReports.map(r => newmap[r.label] = r);
            reportsByName = newmap;
        }
        if (reportsByName.hasOwnProperty(name)) {
            return reportsByName[name];
        }
        return result;
    }
    const ensureReportByName = function (name: string): ISystemReport {
        var result: ISystemReport = defaultReport;
        if (!reportsByName) {
            var newmap: any = {};
            availableReports.map(r => newmap[r.label] = r);
            reportsByName = newmap;
        }
        if (reportsByName.hasOwnProperty(name)) {
            return reportsByName[name];
        }
        return result;
    }


    const getReportByValue = function (value: string): ISystemReport | null {
        var result: ISystemReport | null = null;
        if (!reportsByValue) {
            var newmap: any = {};
            availableReports.map(r => newmap[r.label] = r);
            reportsByValue = newmap;
        }
        if (reportsByValue.hasOwnProperty(value)) {
            return reportsByName[value];
        }
        return result;
    }


    //local hooks
    const [selectedReport, setSelectedReport] = React.useState<ISystemReport>(defaultReport);
    const [selectedReportName, setSelectedReportName] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedReportYear, setSelectedReportYear] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedReportFormat, setSelectedReportFormat] = React.useState<{ value: string; label: string; }>(defaultValue);

    const [selectedReportMonth, setSelectedReportMonth] = React.useState<{ value: string; label: string; }>(monthOptions[0]);
    const [selectedReportYearValue, setSelectedReportYearValue] = React.useState<string>('');
    const [reportTaxonomyType, setReportTaxonomyType] = React.useState<TaxonomyTypeEnum>(TaxonomyTypeEnum.AFR);
    const [yearErrorMessage, setYearErrorMessage] = React.useState<string | null>(null);
    const [formatErrorMessage, setFormatErrorMessage] = React.useState<string | null>(null);

    const [yearOptions, setYearOptions] = React.useState<Array<{ value: any, label: any }>>([]);



    //functions
    function isCheckedReportName(reportNameValue: string): boolean {
        var result = false;

        if (reportNameValue) {
            if (reportNameValue === selectedReportName.label) {
                result = true;
            }
        }

        return result;
    }

    function isAnyFormatSelected() {
        return selectedReportFormat.value.length > 0;
    }
    function isReportFormatSelected(reportFormatValue: string): boolean {
        var result = false;

        if (reportFormatValue) {
            if (selectedReportFormat.value == reportFormatValue) {
                result = true;
            }
        }
        return result;
    }

    function isReportYearSelected(reportYearValue: string): boolean {
        var result = false;

        if (reportYearValue) {
            if (selectedReportYear.value == reportYearValue) {
                result = true;
            }
        }
        return result;
    }

    const reportReadyToGenerate = (): boolean => {

        let result: boolean = false;

        if ((selectedReportYear.value != defaultValue.value &&
            selectedReportFormat.value != defaultValue.value &&
            selectedReportName.value != defaultValue.value &&
            (selectedReportName.value != 'AFRSSUBMITTEDWITHEMERGENCY'))
            || (selectedReportName.value != defaultValue.value &&
                (selectedReportName.value == 'AFRSSUBMITTEDWITHEMERGENCY') &&
                selectedReportYearValue.length >= 4 &&
                selectedReportMonth.value != "0")) {
            result = true;
        }

        return result;
    }

    function isReportDisabled(reportNameValue: string): boolean {
        var result = true;
        if (!reportNameValue || reportNameValue === "default") {
            return result;
        }
        var selectedReport: ISystemReport | null = getReportByName(reportNameValue);
        if (!selectedReport) {
            return result;
        }
        var selectedYear = Number(selectedReportYear.value);
        if (selectedYear <= 0) {
            return result;
        }
        if (selectedYear < selectedReport.effectiveYear) {
            return result;
        }
        return false;
    }
    function isReportFormatDisabled(selectedReport: ISystemReport): boolean {
        var repFormat = selectedReportFormat.value;
        if (repFormat.length === 0) {
            return true;
        }
        if (!selectedReport.outputRestrictions) {
            return false;
        }

        return selectedReport.outputRestrictions.indexOf(repFormat) < 0;
    }

    function hasValidYear(selectedReport: ISystemReport): boolean {
        if (selectedReport.label === "default") {
            return true;
        }
        var selYear = selectedReportYear.value;
        if (selYear.length === 0) {
            return false;
        }
        var iSelYear: number = parseInt(selYear);
        if (iSelYear < selectedReport.effectiveYear) {
            return false;
        }
        return true;
    }
    function hasValidFormat(selectedReport: ISystemReport): boolean {
        if (selectedReport.label === "default") {
            return true;
        }
        var selFormat = selectedReportFormat.value;
        if (selFormat.length === 0) {
            return false;
        }
        if (!selectedReport.outputRestrictions) {
            return true;
        }
        if (selectedReport.outputRestrictions.indexOf(selFormat) < 0) {
            return false;
        }
        return true;
    }


    async function startReport(systemReportParameter: SystemReportParameter) {

        const response = await dispatch(getSystemReport(systemReportParameter));
        const doc = unwrapResult(response);
        if (doc != null) {
            var fileName = systemReportParameter.reportName.replace("CRAZERO", "SDZERO");
            if (selectedReportYear.value) {
                fileName += "_" + selectedReportYear.value;
            }
            switch (systemReportParameter.reportFormat) {
                case "XHTML":
                    fileName += ".xhtml";
                    break;
                case "IXBRL":
                    fileName += ".xhtml";
                    break;
                case "PDF":
                    fileName += ".pdf";
                    break;
                case "EXCEL":
                    fileName += ".xlsx";
                    break;
            }
            //TODO make this a generic function in document slice
            //that takes mime type, base64 encoded string, filename
            //and returns a blob.
            //TODO expand to handle xhtml and excel files
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(otho => {

                    const data = window.URL.createObjectURL(otho);
                    var link = document.createElement("a");
                    link.href = data;
                    link.download = fileName; //"report.xhtml";
                    link.click();
                    setTimeout(function () {
                        // For Firefox it is necessary to delay revoking the ObjectURL
                        window.URL.revokeObjectURL(data);
                    }, 100);
                });
        }

        return doc;

    }

    const handleReportSelectionChange = function (selItem: any) {
        setFormatErrorMessage(null);

        switch (selItem.value) {
            case "CR_29_PUBLICREPORT":
                setReportTaxonomyType(TaxonomyTypeEnum.COURT_REPORTING_29);
                setSelectedReportFormat({ label: "PDF", value: "PDF" });
                break;

            case "CR_939_PUBLICREPORT":
                setReportTaxonomyType(TaxonomyTypeEnum.COURT_REPORTING_939);
                setSelectedReportFormat({ label: "PDF", value: "PDF" });
                break;

            case "SUBMITTEDORVERIFIEDWITHVALIDATIONERRORS":
                setReportTaxonomyType(TaxonomyTypeEnum.AFR);
                setSelectedReportFormat({ label: 'Excel', value: 'EXCEL' });
                break;

            default:
                setReportTaxonomyType(TaxonomyTypeEnum.AFR);
                break;
        }
        setSelectedReportName({ value: selItem.value, label: selItem.name });
    }

    const startCannedReport = async (reportValue: string) => {
        var reportYearValue;

        if (reportValue != 'AFRSSUBMITTEDWITHEMERGENCY') {
            reportYearValue = parseInt(selectedReportYear.value);
        } else {
            var yearMonthString = selectedReportYearValue + selectedReportMonth.value.padStart(2, '0');
            reportYearValue = parseInt(yearMonthString);
        }
        let systemReportParameter: SystemReportParameter = {
            afrYear: reportYearValue,
            reportFormat: selectedReportFormat.value,
            reportName: reportValue
        };

        try {
            await startReport(systemReportParameter);
        } catch (err) {
            console.log(err);
        }
    }

    const handleCannedReportClick = async () => {

        var reportYearValue;

        if (selectedReportName.value != 'AFRSSUBMITTEDWITHEMERGENCY') {
            reportYearValue = parseInt(selectedReportYear.value);
        } else {
            var yearMonthString = selectedReportYearValue + selectedReportMonth.value.padStart(2, '0');
            reportYearValue = parseInt(yearMonthString);
        }
        let systemReportParameter: SystemReportParameter = {
            afrYear: reportYearValue,
            reportFormat: selectedReportFormat.value,
            reportName: selectedReportName.value
        };

        try {
            await startReport(systemReportParameter);
        } catch (err) {
            console.log(err);
        }

    }

    const isCannedReportNotReady = (): Boolean => {
        let result: boolean = false;
        if (selectedReportYear.value == defaultValue.value ||
            selectedReportFormat.value == defaultValue.value ||
            selectedReportName.value == defaultValue.value ||
            isReportDisabled(selectedReportName.label)) {
            result = true;
        }
        return result;
    }

    const isCustomReportNotReady = (): Boolean => {
        let result: boolean = false;
        if (
            selectedReportFormat.value == defaultValue.value ||
            selectedReportYearValue.length < 4 ||
            selectedReportMonth.value == "0") {
            result = true;
        }
        return result;
    }

    React.useEffect(() => {
        setFormatErrorMessage(null);
        setYearErrorMessage(null);
        setSelectedReport(defaultReport);
        if (reportGroup === "COURT_REPORTING") {
            setSelectedReportFormat(reportFormats[0]);
        }
        setSelectedReportName(defaultValue);
        setReportGenerationStatus("");
    }, [reportGroup])

    const filter939Option = (option: { label: string; value: string; }) =>

        ((selectedReportName.value != "CR_939_PUBLICREPORT" && selectedReportName.value != "CR_939_STATUSES")
               && Number(option.value) <= maxAvailableReportYearValue)
        || (selectedReportName.value == "CR_939_PUBLICREPORT" && Number(option.value) <= max939YearValue
            || selectedReportName.value == "CR_939_STATUSES" && Number(option.value) <= max939YearValue)


    React.useEffect(() => {

        if (availableReports && availableReportYears.length > 0) {

            let allReportYears: Array<number> = [];

            for (let i = 0; i < availableReportYears.length; i++) {
                allReportYears[i] = availableReportYears[i];
            }

            if (max939YearValue > 0) {
                const maxAvailableYearValue = Math.max(...availableReportYears);
                if (max939YearValue > maxAvailableYearValue) {
                    allReportYears.push(max939YearValue);
                }
            }

            setYearOptions(allReportYears.map((year: any) => ({
                value: year,
                label: year
            })).reverse())

        }

    }, [availableReportYears, max939YearValue])


    React.useEffect(() => {
        if (selectedReportStatus !== "loading") {
            setReportGenerationStatus(reportGenerationStatus.replace("Started ", "Done "));
        }
    }, [selectedReportStatus])

    React.useEffect(() => {

        let currentDate = new Date();
        let currentYear = currentDate.getFullYear();
        let currentMonth = currentDate.getMonth();

        if (currentMonth == 0) {
            currentMonth = 12;
            currentYear -= 1;
        }

        setSelectedReportYearValue(currentYear.toString());
        var foundReportMonthOption = monthOptions.find((element: any) => element.value == currentMonth.toString());
        if (foundReportMonthOption) {
            setSelectedReportMonth(foundReportMonthOption);
        }

        if (!(availableReportYears) || availableReportYears.length == 0) {

            dispatch(getReportYears(TaxonomyTypeEnum.AFR));

            dispatch(getMax939Year()).then((result: any) => {
                var originalPromiseResult: number = unwrapResult(result);

                setMax939YearValue(originalPromiseResult);
            });
        }

    }, [])

    React.useEffect(() => {

        dispatch(getReportYears(reportTaxonomyType));
    }, [reportTaxonomyType])

    //layouts
    const reportFormatRender = (<React.Fragment>
        <Row style={{ marginLeft: ".3em" }}>
            {reportFormats.map(({ value, label }) => (
                <div
                    hidden={(((selectedReportName.value == 'CR_939_PUBLICREPORT' || (selectedReportName.value == 'CR_29_PUBLICREPORT')) && (value != "PDF"))
                        || (selectedReportName.value == 'SUBMITTEDORVERIFIEDWITHVALIDATIONERRORS' && value == "PDF"))}>
                    <Col>
                        <Input
                            key={value}
                            type="radio"
                            value={value}
                            name="system-report-formats"
                            onChange={(e) => {
                                setSelectedReportFormat({ value: value, label: label });
                            }}
                            checked={isReportFormatSelected(value)}

                        />
                        {label}
                    </Col>
                </div>
            ))}
        </Row>
    </React.Fragment>);

    const addReport = function (r: ISystemReport, isAdmin: boolean, reportGroup: string, results: ISystemReport[]) {
        if (!isAdmin && r.adminOnly) {
            return;
        }
        if (reportGroup !== "ALL" && r.group !== reportGroup) {
            return;
        }
        results.push(r);
    }
    const getReports = function (reportGroup: string): ISystemReport[] {
        var isAdmin: boolean = false;
        if (user && user.role && user.role == UserRoleEnum.Admin) {
            isAdmin = true;
        }
        var results: ISystemReport[] = [];
        availableReports.map((r: any) => addReport(r, isAdmin, reportGroup, results));
        return results;
    }

    const handleReportSelectorClick = function (sel: { label: string, value: string }) {
        handleReportSelectionChange({ name: sel.label, value: sel.value });
        var rep: ISystemReport = ensureReportByName(sel.label);
        setSelectedReport(rep);
        if (isReportDisabled(rep.label) || isReportFormatDisabled(rep)) {
            setReportGenerationStatus(" - Invalid " + sel.label);
            if (!hasValidYear(rep)) {
                var selYear = selectedReportYear.value;
                if (selYear.length === 0) {
                    setYearErrorMessage("Please choose a report year.");
                } else {
                    setYearErrorMessage("Minimum year must be " + rep.effectiveYear);
                }
            } else {
                setYearErrorMessage(null);
            }
            if (!hasValidFormat(rep)) {
                if (rep.outputRestrictions && (rep.outputRestrictions.indexOf(",") < 0)) {
                    // There is only one format allowed - so select it
                    var fmt: string = rep.outputRestrictions;
                    setSelectedReportFormat({ value: fmt, label: fmt });
                    setFormatErrorMessage(null);
                } else {
                    if (!isAnyFormatSelected()) {
                        setFormatErrorMessage("Please choose a format");
                    } else if (rep.outputRestrictions) {
                        setFormatErrorMessage("Format must be one of " + rep.outputRestrictions);
                    } else {
                        setFormatErrorMessage(null);
                    }
                }
            }
            //            setModalSubmissionIsOpen(true);
        } else {
            setReportGenerationStatus(" - Started " + sel.label);
            setYearErrorMessage(null);
            setFormatErrorMessage(null);
            startCannedReport(sel.value);
        }
    }
    const isSelectedReport = function (repValue: string): boolean {
        if (selectedReportName.value.length === 0) {
            return false;
        }
        return selectedReportName.value === repValue;
    }
    const ReportSelector = function (props: { report: ISystemReport }) {
        var rep = props.report;
        var statusMessage: string | null = null;
        var icon: string = rep.icon.indexOf("fa-") >= 0 ? "fa " + rep.icon : "bi " + rep.icon; //"fa fa-balance-scale";
        const [tooltipOpen, setTooltipOpen] = React.useState(false);

        const toggle = () => setTooltipOpen(!tooltipOpen);
        var iconstyle: any = { fontSize: '3rem' };
        if (icon.indexOf("fa ") >= 0) {
            iconstyle["marginBottom"] = "1.4rem";
        }
        var statusStyle: any =
        {
            fontStyle: 'italic'
        };
        var infostyle: any =
        {
            fontSize: '2rem',
            float: "right"
        };
        var buttonStyle: any = {
            /*background:'#458bca',*/
            borderRadius: '1em',
            textAlign: 'center',
            height: '11em',
            width: '16em',
            marginBottom: '2em',
            float: "left"
        }
        var isCurrentlySelected = selectedReportName && selectedReportName.value === rep.value;
        if (isCurrentlySelected) {
            buttonStyle.borders = "2px solid red";
            infostyle.color = "#13FF00";
        }
        if (!isReportDisabled(rep.label)) {
            if (isReportFormatDisabled(rep)) {
                iconstyle.color = "yellow";
                if (formatErrorMessage && isCurrentlySelected) {
                    statusMessage = formatErrorMessage;
                }
            } else {
                iconstyle.color = "#13FF00";
                statusMessage = "Ready to download";
                statusStyle.color = "white";
            }
        } else {
            if (isCurrentlySelected) {
                if (yearErrorMessage) {
                    statusMessage = yearErrorMessage;
                }
                if (formatErrorMessage) {
                    if (statusMessage) {
                        statusMessage = statusMessage + "<br />" + formatErrorMessage;
                    } else {
                        statusMessage = formatErrorMessage;
                    }
                }
            }
        }


        function handleRefreshReportClick(reportValue: string, reportFormat: string, reportYear: string) {

            var reportYearInt;
            reportYearInt = parseInt(reportYear);

            if (reportYearInt) {
                let systemReportParameter: SystemReportParameter = {
                    afrYear: reportYearInt,
                    reportFormat: reportFormat,
                    reportName: reportValue
                };

                dispatch(refreshLongRunningReport(systemReportParameter));
            }
        }

        return (
            <div >
                <Row>
                    <Col>
                        <Button className="reportLinkButton"
                            style={buttonStyle}
                            onClick={(e: any) => { handleReportSelectorClick({ label: rep.label, value: rep.value }) }}
                            disabled={selectedReportStatus === "loading"}
                        >
                            <Row>
                                <Col>
                                    {rep.iconOverride ? <FontAwesomeIcon icon={rep.iconOverride} style={iconstyle} /> : <i className={icon} style={iconstyle}></i>}
                                    <i className="bi bi-info-circle" id={rep.value} style={infostyle}></i>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <h6>{rep.label}</h6>
                                    {statusMessage &&
                                        <h6 style={statusStyle} dangerouslySetInnerHTML={{ __html: "<span>" + statusMessage + "</span>" }} ></h6>
                                    }
                                </Col>
                            </Row>
                            <Tooltip
                                placement="top"
                                isOpen={tooltipOpen}
                                target={rep.value}
                                toggle={toggle}
                            >
                                {rep.documentation}
                            </Tooltip>
                        </Button>
                        <Spinner children={''} style={{ width: '1rem', height: '1rem', float: "right" }}
                            type="grow" color="warning"
                            hidden={!isSelectedReport(rep.value) || selectedReportStatus != "loading"} />
                    </Col>
                </Row>
                <Collapse isOpen={props.report.value == "SUBMITTEDORVERIFIEDWITHVALIDATIONERRORS" && (selectedReportFormat.value == "EXCEL" || selectedReportFormat.value == "EXCEL")}>
                    <Row>
                        <Col>
                            <Button onClick={(e: any) => { handleRefreshReportClick(props.report.value, selectedReportFormat.value, selectedReportYear.value); }}>Refresh</Button>
                        </Col>
                    </Row>
                </Collapse>
            </div>
        );
    }

    const reportNamesRender = () => (
        <React.Fragment>
            <Row>
                <Col>
                    <strong>
                        Select a Report {reportGenerationStatus}
                    </strong>
                </Col>
            </Row>
            <Container fluid>
                <Row style={{
                    paddingTop: '1em',
                    display: "flex",
                    alignContent: "flex-start",
                    columnGap: "2em"
                }}>
                    {getReports(reportGroup).map((rep: ISystemReport) => (
                        <ReportSelector report={rep} ></ReportSelector>
                    ))}
                </Row>
            </Container>
        </React.Fragment>);

    const handleChangeYear = function (event: any, action: any) {
        setYearErrorMessage(null);
        setSelectedReportYear({ label: "", value: event.value });
        if (isReportDisabled(selectedReportName.label) == true) {
            setSelectedReportName(defaultValue);
        }
    }

    const yearSelectionCannedReports = (<React.Fragment>
        <Row>
            <Col>
                <strong>
                    Select a Year
                </strong>
            </Col>
        </Row>
        <Row>
            <Col>
                <Select
                    value={
                        yearOptions.filter(function (option: any) {
                            return option.value === selectedReportYear.value;
                        })
                    }
                    name="Report Year"
                    onChange={handleChangeYear}
                    options={yearOptions}
                    filterOption={filter939Option}
                />
            </Col>
        </Row>
        {yearErrorMessage &&
            <Row>
                <Col>
                    <h4 style={{ color: "red" }}>{yearErrorMessage}</h4>
                </Col>
            </Row>
        }

    </React.Fragment>)

    const yearMonthSelectionCustom = (<React.Fragment>
        <Row>
            <Col>
                <strong>
                    Select a Year and Month
                </strong>
            </Col>
        </Row>
        <Row>
            <Col md="6">
                <Select
                    value={
                        yearOptions.filter(function (option: any) {
                            return option.value === selectedReportYear.value;
                        })
                    }
                    name="Report Year"
                    onChange={handleChangeYear}
                    options={yearOptions}
                    filterOption={filter939Option}
                />
            </Col>
            <Col md="6">
                <Select
                    value={selectedReportMonth}
                    onChange={(e: any) => { setSelectedReportMonth(e); }}
                    options={monthOptions}
                />
            </Col>
        </Row>
        {yearErrorMessage &&
            <Row>
                <Col>
                    <h4 style={{ color: "red" }}>{yearErrorMessage}</h4>
                </Col>
            </Row>
        }

    </React.Fragment>)


    const cannedReportParameters = (<React.Fragment>
        <Row>
            <Col md="3">
                <Collapse isOpen={selectedReportName.value != 'AFRSSUBMITTEDWITHEMERGENCY'}>

                    {yearSelectionCannedReports}

                </Collapse>

                <Collapse isOpen={selectedReportName.value == 'AFRSSUBMITTEDWITHEMERGENCY'}>

                    {yearMonthSelectionCustom}

                </Collapse>
            </Col>
            <Col md="5">
                <Row>
                    <Col>
                        <strong>
                            Select a Format
                        </strong>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {selectedReport.outputRestrictions === "PDF"
                            ?

                            <Label>Court Reports available in .PDF only.</Label>

                            :
                            reportFormatRender
                        }
                    </Col>
                </Row>
                {formatErrorMessage &&
                    <Row>
                        <Col>
                            <h4 style={{ color: "red" }}>{formatErrorMessage}</h4>
                        </Col>
                    </Row>
                }
            </Col>
            <Col md="4">
            </Col>
        </Row>
    </React.Fragment>);

    return (
        <React.Fragment>
            <CardBody>

                {cannedReportParameters}

                <br />

                {reportNamesRender()}

            </CardBody>
        </React.Fragment>
    );
}

export default SystemReports;